import {SyncService} from "./sync-service.service";
import * as firebaseDatabase from "firebase/database";
import * as _ from "lodash";
import {IController, ILogService, IPromise, IQService, IRequestConfig, IScope, ITimeoutService} from "angular";
import {DatabaseReference, DataSnapshot} from "@firebase/database";
import {LocalStoreService} from "./local-store-service.service";
import {SplitService} from "../commons/services/split-service.service";
import {FirebaseChannelResponse} from "./models/firebase-channel-response.model";
import {DataStoreConfig} from "./models/datastore-config.model";
import {PushNotificationsConfig} from "./models/push-notifications-config.model";
import {BringozApi, BringozApiAction} from "./models/bringoz-api.model";

export class BringozArray<T> {

  private _dataStore: DataStoreConfig;
  private _PN: PushNotificationsConfig;
  private _api: BringozApi;
  private _params: { [key: string]: any };
  private _siteId: string; // TODO: Remove
  private _options: any;
  private _cursor: string;
  private _channel: DatabaseReference;

  constructor(private $q: IQService,
              private $log: ILogService,
              private $timeout: ITimeoutService,
              private syncService: SyncService,
              private localStoreService: LocalStoreService,
              private splitService: SplitService,
              api: BringozApi,
              dataStore: DataStoreConfig,
              pushNotificationsConfig?: PushNotificationsConfig) {

    this._dataStore = dataStore;
    this._PN = pushNotificationsConfig;
    this._api = api;
    this._params = {};
    this._siteId = this.splitService.getSiteId(); // TODO: Remove
    this._options = null;
    this._cursor = undefined;
    this._channel = undefined;
  }

  $subscribeChannel(ctrl: IController, cb: (entities: T[]) => any) {

    let databaseStoreName: string = this._PN.database;

    if (!!this._channel) {
      this.syncService.destroyEntityChannel(this._channel, databaseStoreName);
      this._channel = undefined;
    }

    this.syncService.subscribeChannel(databaseStoreName)
      .then((databaseReference: DatabaseReference) => {
        if (databaseReference) {
          this._channel = databaseReference;

          let isInit = true;
          firebaseDatabase.onValue(databaseReference, (snapshot: DataSnapshot) => {

            if (!!isInit) {
              isInit = false;
              return;
            }

            //FIXME
            this.$timeout().then(() => {
              const data = snapshot.val() as FirebaseChannelResponse<T>;
              cb(data.list);
            });
          });
        }
      })
  }

  $subscribeToChannelEvents(cb: (entities: T[], action: 'created' | 'changed' | 'partialChanged') => any): void {

    let self = this;
    let databaseStoreName: string = this._PN.database;

    const functionRef: IPromise<DatabaseReference> = !self._channel
      ? this.syncService.subscribeChannel(databaseStoreName) : this.$q.when(self._channel);

    functionRef
      .then((databaseReference: DatabaseReference) => {
        if (databaseReference) {
          self._channel = databaseReference;

          let isInit = true;
          firebaseDatabase.onValue(databaseReference, (snapshot: DataSnapshot) => {

            if (!!isInit) {
              isInit = false;
              return;
            }

            this.$timeout().then(() => {

              const data = snapshot.val() as FirebaseChannelResponse<T>;
              let list: T[] = data.list;

              this.$log.debug(`[BringozArray] Entity "${databaseStoreName}" "${data.action}"`, list);

              if (data.action === 'partialChanged') {
                const idKey: string = self._dataStore.keyName;
                list = _.chain(list)
                  .filter((_entity) => {
                    const storageEntity = this.localStoreService.getByKey<T>(self._dataStore, _.get(_entity, idKey));
                    return !!storageEntity
                  })
                  .map((_filteredEntity) => {
                    const storageEntity = _.cloneDeep(this.localStoreService.getByKey<T>(self._dataStore, _.get(_filteredEntity, idKey)))
                    _.forOwn(_filteredEntity, (value, key) => {
                      storageEntity[key] = value;
                    })
                    return storageEntity;
                  })
                  .value();
              }

              // this.$log.debug(`[BringozArray] Entity "${databaseStoreName}" "${data.action}"`, list);
              if (list.length) {
                this.syncService.save(this._dataStore, list);
                cb(list, data.action);
              }
            });
          });
        }
      });
  }

  $destroyChannel() {

    let self = this;

    const databaseStoreName = this._PN.database;
    const channel: DatabaseReference = self._channel;

    self._channel = undefined;

    return channel ? this.syncService.destroyEntityChannel(channel, databaseStoreName) : this.$q.when();
  }

  $query(params: { [key: string]: any }, options?) {

    if (_.isUndefined(params)) {
      this.$log.error('[BringozArray] Params resolved to undefined!. --> @$query');
      return this.$q.reject();
    } else {

      if (_.isObject(params)) {

        // Set site id query param to every call.
        // @ts-ignore
        if (!params.siteId) {
          // @ts-ignore
          params.siteId = this.splitService.getSiteId();
        }

        this._params = params;
        this._cursor = undefined;
        this._params.cursor = undefined;

        if (options) {
          this._options = options;
        }

        return this.$q.when();

      } else {
        this.$log.error('[BringozArray] Params is not an object!. --> @$query');
        return this.$q.reject();
      }

    }
  }

  //Cursor
  $next(config: { ignoreFetch?: boolean, getFullData?: boolean, options?: any, data?: any }) {

    let self = this;
    let deferred = this.$q.defer();
    let cursor = self._cursor;

    if (!_.isUndefined(cursor)) {
      _.set(self, '_params.cursor', cursor);
    }

    // Set site id query param to every call.
    let params = self._params;
    params.siteId = this.splitService.getSiteId();

    this.request(self._api.infiniteScroll.address, self, {
      url: self._api.infiniteScroll.address,
      params: params,
      data: config.data,
      method: self._api.infiniteScroll.cmd
    }, config?.options || self._options, true).then(
      (response) => {
        if (!config?.getFullData) {
          response = _.map(response, (element) => {
            return _.get(element, self._dataStore.keyName); // return only ids
          });
        }
        deferred.resolve(response)
      }
    );

    return deferred.promise;
  }

  $put(body: T, params?: { [key: string]: any }) {
    let self = this;

    let url = this._api.base;

    // Set site id query param to every call.
    if (!params) {
      params = {}
    }
    params.siteId = this.splitService.getSiteId();

    return this.request(url, self, {
      url: url,
      method: 'PUT',
      data: body,
      params: params
    });
  }

  $post(body: T) {
    let self = this;

    let url = this._api.base;

    return this.request(url, self, {
      url: url,
      method: 'POST',
      data: body,
      params: {
        siteId: this.splitService.getSiteId()
      }
    });
  }

  $getAll(_params?: { [key: string]: any }, _options?) {

    let self = this;
    let deferred = this.$q.defer();

    if (!_params) {
      _params = {};
    }

    _.set(self, '_params', _params);

    // Set site id query param to every call.
    if (!_params) {
      _params = {}
    }
    if (!_params.siteId) {
      _params.siteId = this.splitService.getSiteId();
    }

    if (_options) {
      _.set(self, '_options', _options);
    }

    this.request(this._api.base, self, {
      url: this._api.base,
      method: 'GET',
      params: _params
    }, _options, true)
      .then((response) => {
        let responseIds = _.map(response, (element) => {
          return _.get(element, self._dataStore.keyName);
        });
        deferred.resolve(responseIds)
      });


    return deferred.promise;
  }

  $asyncValues(ids: string[]): IPromise<T[]> {

    let self = this;

    let deferred = this.$q.defer<T[]>();
    let result: T[] = [];
    let missingIds: string[] = [];

    ids = [].concat(ids);

    _.forEach(ids, (id) => {

      let storeValue = this.localStoreService.getValue<T>(self._dataStore, id);
      if (_.isUndefined(storeValue)) {
        missingIds.push(id);
      } else {
        result.push(storeValue);
      }
    });

    this.getAllObjects(missingIds)
      .then((objectsArray) => {
        result = result.concat(objectsArray);
        this.sort(ids, result).then((sortedObjectsArray) => {
          deferred.resolve(sortedObjectsArray);
        });
      });

    return deferred.promise;
  }

  $values(ids: string[]): T[] {
    return _.map([].concat(ids), (id) => {
      return this.localStoreService.getValue<T>(this._dataStore, id);
    });
  }

  $call(apiCall: BringozApiAction, params: { [key: string]: any }, data: any, options, doUpdate?:boolean) {

    let self = this;

    let url: string = this._api.base + '/' + apiCall.address;
    let updateable: boolean = apiCall.updateable || false;
    let deferred = this.$q.defer();

    // Set site id query param to every call.
    if (!params) {
      params = {}
    }
    params.siteId = this.splitService.getSiteId();

    this.request(url, self, {
      url: url,
      method: apiCall.cmd,
      params: params,
      data: data,
      headers: apiCall.headers,
    }, options, doUpdate || updateable)
      .then(
        (response) => {
          deferred.resolve(response);
        },
        deferred.reject
      );

    return deferred.promise;
  }

  $onCreate(scope: IScope, handler: (entities: T[]) => any): void {
    //FIXME
    scope.$on('ON_CREATE_ENTITIES', (_event, _entities: T[]) => {
      handler(_entities);
    });
  }

  $onChange(scope: IScope, handler: (entities: T[]) => any): void {
    //FIXME
    scope.$on('ON_CHANGE_ENTITIES', (_event, _entities: T[], dataStore: DataStoreConfig) => {
      if (dataStore?.objectStoreName === this._dataStore.objectStoreName) {
        handler(_entities);
      }
    });
  }

  // Force Save - This will indicate that in response, the BringozArray will backup entities
  // via store and return only ids to caller.
  private request(_api: string, _reference, config: IRequestConfig, options?: { [key: string]: string }, doUpdate?: boolean): IPromise<any> {

    let self = _reference;
    let deferred = this.$q.defer();

    this.$log.debug(`[BringozArray] Request "${_api}"`);

    this.syncService.request(_api, config, self._dataStore, options, doUpdate)
      .then((response) => {

          if (typeof _.get(response, 'cursor') === 'string'
            || (!!_.get(response, 'list', false) && _.isEqual(_.keys(response).length, 1))) {
            let list = _.get(response, 'list', []);

            let cursor = _.get(response, 'cursor', null);
            if (_.isEqual(cursor, '') || !list.length) {
              this.$log.debug('[BringozArray] Base API - ' + self._api.base + ', API - ' + _api + '. ' +
                'Response cursor not found or there is no records left in the DB:', response);
            }
            _.set(self, '_cursor', cursor);

            deferred.resolve(list);
          } else {
            deferred.resolve(response);
          }
        }, (error) => {
          this.$log.error('[BringozArray] Base API - \"' + self._api.base + ', API - ' + _api +
            '. \", Error during request :', error);

          deferred.reject(error.data);
        }
      );

    return deferred.promise;
  }

  private getAllObjects(ids: string[]): IPromise<T[]> {

    const deferred = this.$q.defer<T[]>();

    if (ids && ids.length) {
      this.$getAll({
        ids: ids
      }).then((ids: string[]) => {

        let objectsArray = _.map(ids, (id) => {
          return this.localStoreService.getValue<T>(this._dataStore, id);
        });

        deferred.resolve(objectsArray);

      });
    } else {
      deferred.resolve([]);
    }

    return deferred.promise;
  }

  private sort(ids: string[], objectsArray: T[]): IPromise<T[]> {

    const deferred = this.$q.defer<T[]>();
    const idKey = this._dataStore.keyName;

    let result = _.sortBy(objectsArray, [(obj) => {
      return _.indexOf(ids, obj[idKey]);
    }]);

    deferred.resolve(result);
    return deferred.promise;
  }
}
